import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Infinity as InfinityIcon, ArrowRight, Sparkles, Heart } from "lucide-react";
import { ASSETS } from "@/data/assets";

function useCountdown(targetISO) {
  const target = useMemo(() => new Date(targetISO).getTime(), [targetISO]);
  const [diff, setDiff] = useState(() => target - Date.now());
  useEffect(() => {
    const t = setInterval(() => setDiff(target - Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  const s = Math.floor(diff / 1000);
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };
}

// Unified 11·11 + Random Acts section for the home page. 11·11 is the
// "mother" event; Random Acts of Consciousness is the initiative through which
// people participate. Two blocks, one continuous section.
export default function Summit0808Teaser() {
  const now = new Date();
  // 2026-07-27 · Event date shifted from 08·08 (Aug 8) to 11·11 (Nov 11).
  // Roll to next year once the current year's Nov 11 has passed.
  const year =
    now.getMonth() > 10 || (now.getMonth() === 10 && now.getDate() > 11)
      ? now.getFullYear() + 1
      : now.getFullYear();
  const target = `${year}-11-11T00:00:00Z`;
  const c = useCountdown(target);

  return (
    <section
      id="summit"
      data-testid="summit-teaser-section"
      className="relative py-20 md:py-28 cosmic-bg text-docm-cream overflow-hidden grain-dark"
    >
      <div className="pointer-events-none absolute -left-32 top-1/3 -translate-y-1/2 w-[640px] h-[640px] rounded-full border border-docm-gold/10 animate-orbit-spin" />
      <div className="pointer-events-none absolute -right-32 -top-20 w-[520px] h-[520px] rounded-full border border-docm-gold/15 animate-orbit-spin" />

      <div className="relative max-w-[1300px] mx-auto px-5 md:px-10">
        {/* ── 11·11 mother event ──────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 order-2 lg:order-1"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-docm-gold/10 border border-docm-gold/30 backdrop-blur">
              <InfinityIcon size={14} className="text-docm-gold animate-infinity-pulse" />
              <span className="label-eyebrow text-docm-gold">Consciousness Day for Humanity</span>
            </div>

            <h2 className="mt-6 h-display text-[64px] sm:text-[96px] md:text-[120px] lg:text-[140px] leading-[0.9] tracking-tight">
              <span className="gold-text">11·11</span>
            </h2>
            <p className="mt-3 font-display italic text-xl md:text-2xl text-docm-cream/85">
              The Global Day of Consciousness.
            </p>

            <p className="mt-6 max-w-2xl text-base md:text-lg text-docm-cream/75 leading-relaxed font-sans">
              One date. Every time zone. One shared call to action. On 11·11, the world
              is invited to pause — and to perform a single conscious act. Together,
              these simple acts activate the Declaration of Consciousness and turn
              awareness into a shared future.
            </p>

            {/* Compact countdown */}
            <div className="mt-7 grid grid-cols-4 gap-2 md:gap-3 max-w-md">
              {[
                { label: "Days", value: c.days },
                { label: "Hours", value: c.hours },
                { label: "Minutes", value: c.minutes },
                { label: "Seconds", value: c.seconds },
              ].map((u) => (
                <div
                  key={u.label}
                  data-testid={`teaser-countdown-${u.label.toLowerCase()}`}
                  className="rounded-xl glass-dark p-3 md:p-4 text-center"
                >
                  <div className="font-serif-h text-2xl md:text-3xl text-docm-cream tabular-nums">
                    {String(u.value).padStart(2, "0")}
                  </div>
                  <div className="mt-0.5 text-[9px] md:text-[10px] uppercase tracking-[0.22em] text-docm-gold font-semibold">
                    {u.label}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/1111"
                data-testid="teaser-learn-cta"
                className="btn-gold group"
              >
                Learn about 11·11
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/1111#register"
                data-testid="teaser-register-cta"
                className="btn-ghost-gold"
              >
                Register Your Interest
              </Link>
            </div>
          </motion.div>

          {/* Logo column — new square logo fits the circle at 86% width.
              Same design language as Summit0808.jsx hero, smaller max. */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="lg:col-span-5 order-1 lg:order-2 flex justify-center items-end lg:pt-16"
          >
            <div className="relative w-full max-w-[260px] md:max-w-[290px] lg:max-w-[300px] aspect-square">
              <div className="absolute inset-0 rounded-full bg-docm-gold/20 blur-3xl scale-110" aria-hidden="true" />
              <div className="relative w-full h-full rounded-full bg-docm-cream border border-docm-gold/40 shadow-[0_40px_100px_-20px_rgba(201,169,97,0.55)] flex items-center justify-center overflow-hidden">
                <img
                  src={ASSETS.consciousnessDayLogo}
                  alt="11·11 Consciousness Day for Humanity"
                  data-testid="teaser-day-logo"
                  loading="lazy"
                  decoding="async"
                  className="w-[86%] h-[86%] object-contain"
                />
              </div>
            </div>
          </motion.div>
        </div>

        {/* ── Random Acts initiative — nested inside the same section ── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mt-20 md:mt-28 pt-12 md:pt-16 border-t border-docm-gold/15 text-center max-w-4xl mx-auto"
          data-testid="rac-initiative-section"
          id="rac-initiative"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-docm-cream/5 border border-docm-gold/25 backdrop-blur">
            <Sparkles size={14} className="text-docm-gold" />
            <span className="label-eyebrow text-docm-gold">An initiative of 11·11</span>
          </div>

          <h3 className="mt-6 h-display text-3xl sm:text-4xl lg:text-[60px] xl:text-[68px] leading-[1.05] tracking-tight text-docm-cream">
            A{" "}
            <span className="font-display italic text-docm-goldSoft">
              Random Act of Consciousness.
            </span>
          </h3>
          <p className="mt-2 font-display italic text-base md:text-lg text-docm-cream/70">
            The act of 11·11.
          </p>

          <p className="mt-7 max-w-2xl mx-auto text-base md:text-lg text-docm-cream/75 leading-relaxed">
            Small intentional actions, multiplied globally, create meaningful change for people,
            society and the planet. Choose your act. Document it. Inspire someone else to do theirs.
            Together &mdash; we move from awareness to action.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/random-acts"
              data-testid="rac-explore-cta"
              className="btn-gold group"
            >
              Explore Random Acts
              <ArrowRight
                size={16}
                className="transition-transform duration-300 group-hover:translate-x-1"
              />
            </Link>
            <Link
              to="/one-conscious-act"
              data-testid="rac-pledge-cta"
              className="btn-ghost-gold inline-flex items-center justify-center gap-2"
            >
              <Heart size={15} />
              I Pledge to Perform One Conscious Act Today
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

